/**
 * <s-proof-timer> — small urgency line: "Get your proof by [date] if ordered within [time]".
 * Counts down to midnight in the shop's working time zone; the proof date is today (in that zone) plus `days`.
 *
 * Attributes:
 *   time-zone  IANA zone the studio works in (default America/New_York)
 *   days       days until the first proof (default 2)
 *   locale     date language (default the page language)
 *   data-text       copy with [date] and [time] placeholders (from the theme's locale file)
 *   data-time-text  countdown format with [h] [m] [s] placeholders (default "[h]h [m]m [s]s")
 *
 * Re-mounts cleanly when a partial (cart or product form) is swapped (each instance owns its own interval).
 */
class ProofTimer extends HTMLElement {
  #interval;

  connectedCallback() {
    this.#render();
    this.#interval = setInterval(() => this.#render(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
  }

  get #timeZone() {
    return this.getAttribute('time-zone') || 'America/New_York';
  }

  /** Current wall-clock parts in the studio's time zone (avoids the double time-zone shift of re-parsed dates). */
  #zonedParts(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.#timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') };
  }

  #render() {
    const now = this.#zonedParts(new Date());
    const days = Number(this.getAttribute('days')) || 2;

    // Seconds left until midnight in the studio's zone.
    const elapsed = now.hour * 3600 + now.minute * 60 + now.second;
    const left = Math.max(0, 86400 - elapsed);
    const hours = Math.floor(left / 3600);
    const minutes = Math.floor((left % 3600) / 60);
    const seconds = left % 60;

    // Proof date: the studio's calendar date plus `days`, formatted as a plain calendar date (UTC noon avoids DST edges).
    const proof = new Date(Date.UTC(now.year, now.month - 1, now.day + days, 12));
    const locale = this.getAttribute('locale') || document.documentElement.lang || 'en-US';
    const dateText = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(proof);
    const pad = (n) => String(n).padStart(2, '0');
    const timeTemplate = this.dataset.timeText || '[h]h [m]m [s]s';
    const timeText = timeTemplate.replace('[h]', String(hours)).replace('[m]', pad(minutes)).replace('[s]', pad(seconds));

    const template = this.dataset.text || 'Get your proof by [date] if ordered within [time]';
    const [before, afterDate = ''] = template.split('[date]');
    const [between, after = ''] = afterDate.split('[time]');

    const strongDate = document.createElement('strong');
    strongDate.className = 'font-semibold text-on-surface-secondary';
    strongDate.textContent = dateText;
    const time = document.createElement('span');
    time.className = 'font-medium tabular-nums';
    time.textContent = timeText;

    const target = this.querySelector('[data-proof-timer-text]') || this;
    target.replaceChildren(before, strongDate, between, time, after);
    this.hidden = false;
  }
}

if (!customElements.get('s-proof-timer')) customElements.define('s-proof-timer', ProofTimer);
